import { Box, VStack } from '@chakra-ui/react';
import { BaseAlert, BaseAlertProps } from '@invara-dev/react-base';

type Props = Pick<BaseAlertProps, 'status'> & {
  header: string;
  toDisplay: string[];
};
/**
 * InformationAlertDisplay component renders a series of alerts for provided messages.
 *
 * It maps over the `toDisplay` array, creating a `BaseAlert` for each note,
 * displaying a bold header and the message body.
 *
 * @component
 * @param {{ status: BaseAlertProps['status']; header: string; toDisplay: string[] }} props
 * @param {BaseAlertProps['status']} props.status - The alert status (e.g., 'error', 'warning', 'info').
 * @param {string} props.header - Title text displayed in bold above each note.
 * @param {string[]} props.toDisplay - Array of message strings to render as individual alerts.
 * @returns {JSX.Element|null} A container of alerts or null if `toDisplay` is empty.
 */

export const InformationAlertDisplay = ({ status, header, toDisplay }: Props) => {
  if (!toDisplay || toDisplay.length === 0) {
    return null;
  }

  return (
    <BaseAlert status={status}>
      <VStack gap='0px' alignItems='flex-start'>
        <Box fontWeight='bold'>{header}</Box>
        <Box as='ol'>
          {toDisplay.map((note, index) => (
            <Box as='li' key={index}>
              {note}
            </Box>
          ))}
        </Box>
      </VStack>
    </BaseAlert>
  );
};
